import React, { useState, useEffect } from "react";
import AsyncCreatableSelect from "react-select/async-creatable";
import { useDispatch } from "react-redux";
import { searchMedicalConditions } from "../../../../../redux/modules/clinical-services/actions";
import { customReactSelectStyles } from "./medication-select";

const MedicalConditionSelect = ({
  type = "Medical Condition",
  onChange = () => {},
  isRequired = false,
  onNewCreate = () => {},
  error = false,
  value = { value: "", label: "" },
  defaultOptions = [],
  label = "",
  disabled = false,
  patientMedicalConditions = [],
  patientMedicalStudies = [],
  patientMedicalSurgeries = [],
  class2,
  isClearable = true,
  showDropdownIndicator = true,
  // opt-in: when true, render the select menu into document.body (useful inside modals/overflowing containers)
  useMenuPortal = false,
}) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [showError, setShowError] = useState(false);
  const dispatch = useDispatch();
  let timeoutId;

  const fetchCondition = async (inputValue) => {
    const filteredDefaultOptions = defaultOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );

    if (!inputValue) {
      return filteredDefaultOptions;
    }

    const results = await dispatch(searchMedicalConditions(inputValue));

    if (!results || !Array.isArray(results)) {
      return filteredDefaultOptions;
    }

    const formattedResults = results.map((item) => ({
      value: item.id,
      label: item.name,
    }));

    // Combine filtered default options with search results, ensuring no duplicates
    const combinedOptions = [
      ...filteredDefaultOptions,
      ...formattedResults.filter(
        (result) =>
          !filteredDefaultOptions.some(
            (option) => option.value === result.value
          )
      ),
    ];

    return combinedOptions;
  };

  const promiseOptions = (inputValue) =>
    new Promise((resolve) => {
      // Clear the previous timeout, if any
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Set a new timeout
      timeoutId = setTimeout(async () => {
        resolve(await fetchCondition(inputValue));
      }, 300);
    });

  const handleCreate = (inputValue) => {
    setTimeout(() => {
      const newOption = {
        label: inputValue,
        value: inputValue,
      };
      setSelectedOption(newOption);
      setShowError(isRequired && !newOption.value);
      onChange(newOption);
      onNewCreate(true);
    }, 1000);
  };

  const handleChange = (selectedOption) => {
    setSelectedOption(selectedOption);
    setShowError(isRequired && !selectedOption);
    onChange(selectedOption);
    if (selectedOption && selectedOption.value) {
      onNewCreate(false);
    }
  };

  useEffect(() => {
    if (value?.value) {
      setSelectedOption(value);
    } else {
      setSelectedOption(null);
    }
  }, [value]);
  // Effect to fetch options based on the selected medicine's first three letters
  useEffect(() => {
    setShowError(error);
  }, [error]);

  return (
    <div className="w-full col-span-12">
      <div
        className={`relative rounded-[4px] border-[1px]  ${
          disabled && "opacity-60"
        } 
        ${!disabled && isRequired && selectedOption === null ? "bg-blue-100" : "bg-white"}
         ${
           showError && isRequired ? "border-red-200" : "border-gray-100"
         } px-3 py-3 focus-within:border-REELBlue focus-within:border-[2px] 
        ${class2}`}
      >
        <div className="">
          <span className="block mb-1 text-gray-900 text-body-xs">
            {label ? label : "Medical Condition"}{" "}
            {isRequired && <span className="">*</span>}
          </span>
          <AsyncCreatableSelect
            loadOptions={promiseOptions}
            defaultOptions={defaultOptions}
            styles={
              useMenuPortal
                ? {
                    ...customReactSelectStyles(isRequired, selectedOption),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }
                : customReactSelectStyles(isRequired, selectedOption)
            }
            value={selectedOption}
            onChange={handleChange}
            isDisabled={disabled}
            onCreateOption={handleCreate}
            placeholder={`Select ${label || type}`}
            noOptionsMessage={() => `No ${label || "conditions"} found`}
            isSearchable={true}
            isClearable={isClearable}
            components={showDropdownIndicator ? undefined : { DropdownIndicator: null, ClearIndicator: null }
            }
            menuPortalTarget={useMenuPortal && typeof window !== "undefined" ? document.body : undefined}
            menuPosition={useMenuPortal ? "fixed" : "absolute"}
          />
        </div>
      </div>
      {showError && isRequired && (
        <span className="text-red-200 text-xs">
          {label ? label : "Medical Condition"} is required
        </span>
      )}
    </div>
  );
};

export default MedicalConditionSelect;
